<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import { useRoute } from 'vue-router'

import {
  failureState,
  pendingState,
  doneState
} from '@/components/scenario-loading/constants/progressState'

import { useCalculationPolling } from '@/components/scenario-loading/composables/useScenarioPolling'

import { useScenarioStore } from '@/stores/scenario'

import { useProjectStore } from '@/stores/project'
import ErrorCardLoading from '@/components/scenario-loading/ErrorCardLoading.vue'
import PendingCardLoading from '@/components/scenario-loading/PendingCardLoading.vue'
import StepProgressLinear from '@/components/scenario-loading/StepProgressLinear.vue'

const route = useRoute()

const emit = defineEmits(['updateCalculationStatus'])

const scenarioStore = useScenarioStore()
const { currentItem } = storeToRefs(scenarioStore)
const statusChoices = ref<{ value: string; display_name: string }[]>([])
const { calculationProgressInfo, steps, startPolling, stopPolling } = useCalculationPolling()
const projectStore = useProjectStore()
const showLoadingResultTitle = ref(false)
const statusChecked = ref(false)

onMounted(async () => {
  if (scenarioId.value) {
    await startPolling(scenarioId.value)
  }
  const options = await scenarioStore.getScenarioOptions?.()
  statusChoices.value = options?.actions?.POST?.status?.choices || []
})

watch(
  () => currentItem.value?.status,
  (status) => {
    const doneStatusValue =
      statusChoices.value.find((choice) => choice.display_name === doneState)?.value || doneState
    if (status !== undefined && status !== null && status !== '') {
      showLoadingResultTitle.value = status === doneStatusValue
      statusChecked.value = true
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  stopPolling()
})

const currentStepIndex = computed(() => {
  const message = calculationProgressInfo.value?.message
  if (!message) return -1
  return steps.value.findIndex((step) => step === message)
})

const getStepPercent = (index: number): number => {
  if (currentStepIndex.value === -1) return 0
  if (index < currentStepIndex.value) return 100
  if (index === currentStepIndex.value) return calculationProgressInfo.value?.percent ?? 0
  return 0
}

watch(
  calculationProgressInfo,
  (newValue) => {
    emit('updateCalculationStatus', newValue?.state)
  },
  { deep: true }
)

const scenarioId = computed(() => {
  const param = route.params.scenarioId
  return parseInt(Array.isArray(param) ? param[0] : param)
})

const errorStatusValue = computed(() => {
  return statusChoices.value.find((choice) => choice.display_name === 'Error')?.value || 'Error'
})

const navigateToProject = () => {
  if (currentItem.value?.project) {
    router.push({ name: 'project-detail', params: { id: currentItem.value.project } })
  }
}

const isError = computed(() => {
  return (
    currentItem.value?.status === errorStatusValue.value ||
    calculationProgressInfo.value?.state === failureState
  )
})
const navigateToInput = async () => {
  if (currentItem.value) {
    const projectId = currentItem.value.project
    await projectStore.fetchItem(projectId)
    router.push({
      name: 'scenario-form',
      params: {
        id: projectId,
        scenarioId: currentItem.value.id
      },
      query: {
        projectName: projectStore.currentItem?.name || 'Unknown Project'
      }
    })
  }
}
</script>

<template>
  <v-container max-width="850" class="py-10">
    <v-card elevation="0" flat>
      <template v-if="isError">
        <ErrorCardLoading
          class="mt-8"
          @back-to-project="navigateToProject"
          @back-to-calculation-detail="navigateToInput"
        />
      </template>

      <template v-else>
        <v-card-title v-if="statusChecked" class="text-h5 text-center">
          <template v-if="showLoadingResultTitle">
            Loading Result for <strong>{{ currentItem?.name }}</strong
            >...
          </template>
          <template v-else>
            Generating results for <strong>{{ currentItem?.name }}</strong
            >...
          </template>
        </v-card-title>
        <!-- ...rest of template... -->
        <template v-if="calculationProgressInfo?.state === pendingState">
          <PendingCardLoading class="mt-4" />
        </template>
        <template v-else>
          <v-card-subtitle class="text-center mb-2">
            Please wait while we process your simulation. This may take a few moments.
          </v-card-subtitle>
          <v-card-text class="mt-4">
            <StepProgressLinear
              v-for="(step, index) in steps"
              :key="index"
              :step="step"
              :is-current-step="index === currentStepIndex"
              :step-percent="getStepPercent(index)"
              :indeterminate="!getStepPercent(index) && index === currentStepIndex"
            ></StepProgressLinear>
          </v-card-text>
        </template>
      </template>
    </v-card>
  </v-container>
</template>
